import { type FC } from "react";
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import {
  AudienceFieldType,
  type AudienceConditionInputType,
  type AudienceOpType,
  type MarketingLanguageType,
} from "@/types/graphql/partner.generated";
import { ProductsAutocomplete } from "@/partner/entities/products";
import { CountryCodeField } from "@/partner/shared/ui";
import { LinksAutocomplete } from "@/partner/features/links/ui/LinksAutocomplete";
import { EmailCampaignsAutocomplete } from "@/partner/features/automations/ui/EmailCampaignsAutocomplete";
import {
  AUDIENCE_FIELD_OPS,
  changeCondition,
  conditionIssue,
  LANGUAGE_LABEL_KEYS,
  MARKETING_LANGUAGES,
  valueKind,
  type MarketingLimits,
} from "@/partner/entities/marketing";
import { AUDIENCE_FIELD_KEYS, audienceOpKey } from "../model/audienceLabels";

/**
 * Ширина контейнера условий (не окна), с которой строка встаёт в одну
 * линию: поле 220 + операция 180 + значение не уже ~180 + кнопка и
 * промежутки. Контейнер объявлен в AudienceFilterBuilder
 * (containerType: inline-size).
 */
export const AUDIENCE_ROW_WIDE_MIN_PX = 640;
const WIDE = `@container (min-width: ${AUDIENCE_ROW_WIDE_MIN_PX}px)`;

interface AudienceConditionRowProps {
  condition: AudienceConditionInputType;
  onChange: (condition: AudienceConditionInputType) => void;
  onRemove: () => void;
  disabled: boolean;
  /** Пределы значений с бэкенда; null, пока их нет. */
  limits: MarketingLimits | null;
  /** Поля, из которых выбирается условие (AudienceFilterBuilder). */
  fields: readonly AudienceFieldType[];
  /** Письма выше шага условия цепочки для EMAIL_OPENED и EMAIL_CLICKED. */
  emailNodes: readonly { id: string; label: string }[];
}

/**
 * Условие фильтра аудитории: поле, операция и редактор значения по виду
 * (valueKind). Смена поля или операции сбрасывает значение
 * (changeCondition), ошибка значения выводится под редактором.
 *
 * Раскладка по ширине контейнера, а не окна (финальное ревью C2): одна
 * строка и в широкой карточке рассылки, и в узкой панели шага
 * автоматизации (331 и 416 px) иначе вылезала за карточку, и значение с
 * кнопкой удаления обрезались. Узкий контейнер: сетка из двух колонок,
 * поле, операция и значение друг под другом на всю ширину, кнопка удаления
 * справа от поля. Широкий (от AUDIENCE_ROW_WIDE_MIN_PX): прежняя строка.
 */
export const AudienceConditionRow: FC<AudienceConditionRowProps> = ({
  condition,
  onChange,
  onRemove,
  disabled,
  limits,
  fields,
  emailNodes,
}) => {
  const { t } = useTranslation();
  const kind = valueKind(condition.field, condition.op);
  // Сохранённое поле вне списка (правила сузились) остаётся видимым, иначе
  // <TextField select> не находит своё значение среди пунктов.
  const fieldOptions = fields.includes(condition.field)
    ? fields
    : [...fields, condition.field];
  const issue = conditionIssue(condition, limits);
  const error = Boolean(issue);
  const helperText = issue ? t(issue) : undefined;
  const values = condition.stringValues ?? [];
  const setValues = (stringValues: string[]) =>
    onChange({ ...condition, stringValues });

  const renderBool = () => (
    <TextField
      select
      fullWidth
      label={t("broadcasts.audience.value")}
      value={condition.boolValue === false ? "false" : "true"}
      onChange={(event) =>
        onChange({
          ...condition,
          boolValue: event.target.value === "true",
        })
      }
      disabled={disabled}
    >
      <MenuItem value="true">{t("broadcasts.audience.yes")}</MenuItem>
      <MenuItem value="false">{t("broadcasts.audience.no")}</MenuItem>
    </TextField>
  );

  const renderValue = () => {
    switch (kind) {
      case "bool":
        return renderBool();
      case "emailNode":
        // Узкая панель шага: «да/нет» и письмо друг под другом.
        return (
          <Stack spacing={2}>
            {renderBool()}
            <TextField
              select
              fullWidth
              label={t("broadcasts.audience.emailNode")}
              value={condition.nodeId ?? ""}
              onChange={(event) =>
                onChange({ ...condition, nodeId: event.target.value || null })
              }
              disabled={disabled || emailNodes.length === 0}
              error={error}
              helperText={
                emailNodes.length === 0
                  ? t("broadcasts.audience.noEarlierEmail")
                  : helperText
              }
            >
              {emailNodes.map((node) => (
                <MenuItem key={node.id} value={node.id}>
                  {node.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        );
      case "number": {
        const usd = condition.field === AudienceFieldType.SpentUsdType;
        const adornment = usd
          ? t("broadcasts.audience.usd")
          : t("broadcasts.audience.days");
        return (
          <TextField
            fullWidth
            type="number"
            label={t("broadcasts.audience.value")}
            value={condition.numberValue ?? ""}
            onChange={(event) =>
              onChange({
                ...condition,
                numberValue:
                  event.target.value === "" ? null : Number(event.target.value),
              })
            }
            error={error}
            helperText={helperText}
            disabled={disabled}
            slotProps={{
              htmlInput: { min: 0 },
              input: usd
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        {adornment}
                      </InputAdornment>
                    ),
                  }
                : {
                    endAdornment: (
                      <InputAdornment position="end">
                        {adornment}
                      </InputAdornment>
                    ),
                  },
            }}
          />
        );
      }
      case "products":
        return (
          <ProductsAutocomplete
            label={t(AUDIENCE_FIELD_KEYS[condition.field])}
            value={values}
            onChange={setValues}
            disabled={disabled}
            error={error}
            helperText={helperText}
          />
        );
      case "countries":
        return (
          <CountryCodeField
            label={t(AUDIENCE_FIELD_KEYS[condition.field])}
            value={values}
            onChange={setValues}
            disabled={disabled}
            error={error}
            helperText={helperText}
          />
        );
      case "languages":
        return (
          <Autocomplete<MarketingLanguageType, true>
            multiple
            fullWidth
            disabled={disabled}
            options={MARKETING_LANGUAGES}
            getOptionLabel={(language) => t(LANGUAGE_LABEL_KEYS[language])}
            value={MARKETING_LANGUAGES.filter((l) => values.includes(l))}
            onChange={(_event, next) => setValues(next)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(AUDIENCE_FIELD_KEYS[condition.field])}
                error={error}
                helperText={helperText}
              />
            )}
          />
        );
      case "links":
        return (
          <LinksAutocomplete
            label={t(AUDIENCE_FIELD_KEYS[condition.field])}
            value={values}
            onChange={setValues}
            disabled={disabled}
            error={error}
            helperText={helperText}
          />
        );
      case "emailCampaigns":
        return (
          <EmailCampaignsAutocomplete
            label={t(AUDIENCE_FIELD_KEYS[condition.field])}
            value={values}
            onChange={setValues}
            disabled={disabled}
            error={error}
            helperText={helperText}
          />
        );
      case "tags":
        return (
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            fullWidth
            disabled={disabled}
            options={[]}
            value={values}
            onChange={(_event, next) =>
              setValues(next.map((v) => v.trim()).filter(Boolean))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(AUDIENCE_FIELD_KEYS[condition.field])}
                error={error}
                helperText={helperText}
              />
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        columnGap: 1,
        rowGap: 2,
        alignItems: "start",
        // Узкая раскладка: условия отделены друг от друга чуть сильнее, чем
        // поля одного условия.
        pb: 1,
        [WIDE]: {
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          pb: 0,
        },
      }}
    >
      <TextField
        select
        label={t("broadcasts.audience.fieldLabel")}
        value={condition.field}
        onChange={(event) =>
          onChange(
            changeCondition(condition, {
              field: event.target.value as AudienceFieldType,
            })
          )
        }
        disabled={disabled}
        sx={{ gridColumn: 1, gridRow: 1, [WIDE]: { minWidth: 220 } }}
      >
        {fieldOptions.map((field) => (
          <MenuItem key={field} value={field}>
            {t(AUDIENCE_FIELD_KEYS[field])}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label={t("broadcasts.audience.opLabel")}
        value={condition.op}
        onChange={(event) =>
          onChange(
            changeCondition(condition, {
              op: event.target.value as AudienceOpType,
            })
          )
        }
        disabled={disabled}
        sx={{ gridColumn: 1, gridRow: 2, [WIDE]: { minWidth: 180 } }}
      >
        {AUDIENCE_FIELD_OPS[condition.field].map((op) => (
          <MenuItem key={op} value={op}>
            {t(audienceOpKey(condition.field, op))}
          </MenuItem>
        ))}
      </TextField>
      <Stack
        sx={{
          gridColumn: 1,
          gridRow: 3,
          minWidth: 0,
          [WIDE]: { flex: 1 },
        }}
      >
        {renderValue()}
        {condition.field === AudienceFieldType.PurchasedProductType && (
          // Смысл поля один и в аудитории, и в условии цепочки: любая
          // покупка, включая возвращённые.
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("broadcasts.audience.fieldHint.PURCHASED_PRODUCT")}
          </Typography>
        )}
      </Stack>
      <IconButton
        aria-label={t("broadcasts.audience.removeCondition")}
        onClick={onRemove}
        disabled={disabled}
        // Узкая раскладка: кнопка 40 px по центру поля 56 px справа от него
        // (отступ 8 px сверху). Широкая: как раньше, по центру строки.
        sx={{
          gridColumn: 2,
          gridRow: 1,
          mt: 1,
          flexShrink: 0,
          [WIDE]: { mt: 0, alignSelf: "center" },
        }}
      >
        <DeleteOutlineIcon />
      </IconButton>
    </Box>
  );
};
